import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { getSceneOriginalEpisodeParagraphs } from '../../../domains/scene/services/sceneService';
import type { SceneOriginalEpisodeParagraphsPageResponse } from '../../../domains/scene/services/sceneService';
import { measureEpisodePageCount } from '../utils/measureEpisodePageCount';

const PARAGRAPHS_LIMIT = 100;
const BASE_PARAMS = { limit: PARAGRAPHS_LIMIT, direction: 'after' as const };

/** 헤더 + 하단 바 + safe area로 인해 실제 콘텐츠 영역이 100vh보다 작음. 너무 크면 빈 페이지, 너무 작으면 마지막 잘림 */
const RESERVED_VIEWPORT_HEIGHT = 100;

async function fetchAllParagraphPages(
  sceneId: string,
  episodeId: string
): Promise<SceneOriginalEpisodeParagraphsPageResponse[]> {
  const pages: SceneOriginalEpisodeParagraphsPageResponse[] = [];
  let cursor: number | undefined;
  let hasMore = true;

  while (hasMore) {
    const result = await getSceneOriginalEpisodeParagraphs({
      sceneId,
      episodeId,
      params: { ...BASE_PARAMS, cursor }
    });
    pages.push(result);
    const { hasMore: h, nextCursor } = result.meta?.pagination ?? {};
    hasMore = !!h && nextCursor != null;
    cursor = nextCursor ?? undefined;
  }
  return pages;
}

interface EpisodeLike {
  sceneEpisodeId?: string;
}

interface UseEpisodePagesMeasurementResult {
  episodePageCounts: number[];
  isMeasuring: boolean;
  error: Error | null;
  viewportWidth: number;
}

const createMeasurementHost = (viewportWidth: number) => {
  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-9999px';
  host.style.top = '0';
  host.style.width = `${viewportWidth}px`;
  host.style.height = '100vh';
  host.style.visibility = 'hidden';
  host.style.pointerEvents = 'none';
  host.style.overflow = 'hidden';
  host.style.background = 'transparent';
  return host;
};

const waitForNextFrame = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });

const createEpisodeContentElement = (paragraphs: Array<{ content?: string | null }>) => {
  const content = document.createElement('div');
  content.style.height = '100%';
  content.style.display = 'inline-block';
  content.style.verticalAlign = 'top';
  content.style.overflow = 'hidden';
  content.style.columnWidth = '100vw';
  content.style.columnFill = 'auto';
  content.style.columnGap = '0';
  content.style.width = 'auto';

  const block = document.createElement('div');
  block.style.padding = '0 17px';

  paragraphs.forEach((paragraph) => {
    const p = document.createElement('p');
    p.textContent = paragraph.content ?? '';
    p.style.whiteSpace = 'pre-wrap';
    p.style.fontFamily = '"Noto Serif KR", ui-serif, Georgia, "Times New Roman", serif';
    p.style.fontSize = '18px';
    p.style.lineHeight = '40px';
    block.appendChild(p);
  });

  content.appendChild(block);
  return content;
};

export const useEpisodePagesMeasurement = (
  episodes: EpisodeLike[],
  sceneId: string | undefined
): UseEpisodePagesMeasurementResult => {
  const queryClient = useQueryClient();
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0
  );
  const [episodePageCounts, setEpisodePageCounts] = useState<number[]>([]);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (
      !sceneId ||
      episodes.length === 0 ||
      viewportWidth <= 0 ||
      typeof document === 'undefined'
    ) {
      setEpisodePageCounts([]);
      return;
    }

    let isCancelled = false;
    const episodeIds = episodes.map((episode) => episode.sceneEpisodeId ?? '').filter(Boolean);

    const measure = async () => {
      setIsMeasuring(true);
      setError(null);

      const allPagesData = await Promise.all(
        episodeIds.map((episodeId) => fetchAllParagraphPages(sceneId, episodeId))
      );

      if (isCancelled) return;

      for (let i = 0; i < episodeIds.length; i += 1) {
        const episodeId = episodeIds[i];
        const pages = allPagesData[i];
        const pageParams = pages.map((p, idx) =>
          idx === 0
            ? undefined
            : {
                cursor: pages[idx - 1]?.meta?.pagination?.nextCursor ?? 0,
                direction: 'after' as const
              }
        );
        queryClient.setQueryData(['sceneEpisodeParagraphs', sceneId, episodeId, BASE_PARAMS], {
          pages,
          pageParams
        });
      }

      if (isCancelled) return;

      const host = createMeasurementHost(viewportWidth);
      document.body.appendChild(host);

      const measuredPageCounts: number[] = Array(episodes.length).fill(1);

      try {
        for (let index = 0; index < episodeIds.length; index += 1) {
          const episodeId = episodeIds[index];
          const pages = allPagesData[index];
          const paragraphs = pages.flatMap((p) => p.sceneEpisodeParagraphs ?? []);
          const contentElement = createEpisodeContentElement(paragraphs);
          host.appendChild(contentElement);
          await waitForNextFrame();

          const fullViewportHeight = host.clientHeight || window.innerHeight || 1;
          const effectiveContentHeight = Math.max(
            fullViewportHeight - RESERVED_VIEWPORT_HEIGHT,
            fullViewportHeight * 0.6
          );
          measuredPageCounts[index] = measureEpisodePageCount(
            contentElement,
            effectiveContentHeight
          );
          host.removeChild(contentElement);
        }
      } finally {
        document.body.removeChild(host);
      }

      if (!isCancelled) {
        setEpisodePageCounts(measuredPageCounts);
        setIsMeasuring(false);
      }
    };

    measure().catch((caughtError: unknown) => {
      if (isCancelled) return;
      setError(caughtError instanceof Error ? caughtError : new Error('측정에 실패했습니다.'));
      setEpisodePageCounts(Array(episodes.length).fill(1));
      setIsMeasuring(false);
    });

    return () => {
      isCancelled = true;
    };
  }, [episodes, sceneId, viewportWidth, queryClient]);

  return { episodePageCounts, isMeasuring, error, viewportWidth };
};
